import { defineStore } from 'pinia';
import { markRaw, type Raw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import {
  IInvoice,
  IPayment,
  IPaymentMethod,
  IPaymentService,
  IShiftService,
  PaymentMethodService,
  PaymentService,
  ShiftService,
  TillService,
  ShiftStatus,
  Till,
  IChangePaymentMethodOption,
  IInvoiceOperationService,
  InvoiceOperationService,
  IPaymentMethodService,
  ITillService,
  SystemError,
  PaymentStatus,
  IDevice,
  GetManyResult,
} from '@posx/core';
import { LitePosPayment, PaymentStatus as LitePaymentStatus, PaymentRequest, PaymentResponse, validateConfig, RefundResponse, RefundStatus, CancelResponse } from '@litepos/payment';
import {
  HirectorClient,
  IHirectorApiConfig,
  IHirectorCreateChargeRequest,
  IHirectorGetChargeRequest,
  IHirectorResponse,
} from '@posx/hirector';
import {
  YeahPayClient,
  IYeahPayApiConfig,
  IYeahPayCreateChargeRequest,
  IYeahPayQueryRequest,
  IYeahPayResponse,
} from '@posx/yeahpay';
import { useDevice } from './device.store';
import { useWyoSdkStore } from '@xpos/crm/src/stores/data';
import { ICharge, IObjectResponse } from '@wyocrm/sdk';
import { resolve } from 'node:path';
import { logError, logInfo, logLiveQueryError, isLiveQueryCursorError } from '@/utils/logger';
import { preciseRound } from '@/utils';
import { captureException } from '@sentry/vue';
import { DataWorker } from '@/workers/data.worker';
import { HubConnectionState } from '@microsoft/signalr';
import { useConfigStore } from './config';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { Capacitor } from '@capacitor/core';
import { getPlatform } from '@/utils/misc';
import { Subscription } from 'dexie';

// Hub message types for CDS communication
export enum HubMessageType {
  PaynowEvent = 'paynow_event',
  SmoochPaymentEvent = 'smooch_payment_event',
  SyncInvoice = 'sync_invoice',
}

export type HubMessage<T> = {
  type: HubMessageType | string;
  data: T;
};

// '/yeahpay-api' only exists as a vite dev proxy; native builds need the absolute gateway
const yeahPayUrl = (path: 'async' | 'sync', override?: string) => getPlatform() === 'web' ? `/yeahpay-api/acceptance/acceptance-mis-pos/${path}` : (override || `https://business.yeahpay.sg/acceptance/acceptance-mis-pos/${path}`);

// Business-layer cap for status polling — platform HTTP timeouts are unreliable
const YEAHPAY_QUERY_TIMEOUT_MS = 15000;
const YEAHPAY_CANCEL_MAX_DURATION_MS = 150000;
const YEAHPAY_CANCEL_ATTEMPT_TIMEOUT_MS = 7000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error(`timeout after ${ms}ms`)), ms); });
  promise.catch(() => {}); // Absorb late reject so it isn't unhandled after race already settled on timeout
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer!));
}

// Build YeahPay client config from device credentials; throws when any credential is missing
function buildYeahPayConfig(device: IDevice): IYeahPayApiConfig {
  const { yeahpay_app_id, yeahpay_device_sn, yeahpay_server_public_key, yeahpay_client_private_key, yeahpay_signature_key } = device.etc;
  if (!yeahpay_app_id || !yeahpay_device_sn || !yeahpay_server_public_key || !yeahpay_client_private_key || !yeahpay_signature_key) throw new SystemError('YeahPay configuration is not found');
  return { asyncUrl: yeahPayUrl('async', device.etc.yeahpay_async_url), syncUrl: yeahPayUrl('sync', device.etc.yeahpay_sync_url as string | undefined), appId: yeahpay_app_id, deviceSn: yeahpay_device_sn, serverPublicKey: yeahpay_server_public_key, clientPrivateKey: yeahpay_client_private_key, signatureKey: yeahpay_signature_key };
}

// Build Hirector (SmoochPay) client config from device keys; baseUrl must end with a slash
function buildHirectorConfig(device: IDevice): IHirectorApiConfig {
  if (!device.etc.hirector_private_key || !device.etc.hirector_public_key) throw new SystemError('Hirector keys are not found');
  return { baseUrl: 'https://api.smoochpay.com/', publicKey: device.etc.hirector_public_key, secretKey: device.etc.hirector_private_key };
}

export const usePaymentStore = defineStore('payment', {
  state: () => ({
    list: [] as IPaymentMethod[],
    selecteds: [] as IPaymentMethod[],
    optionals: [] as IPaymentMethod[],
    visibleStaffShift: false,
    paymentMethodService: null as IPaymentMethodService | null,
    paymentService: null as IPaymentService | null,
    tillService: null as ITillService | null,
    litePosPayment: null as Raw<LitePosPayment> | null,
    _subscription: null as Subscription | null,
  }),
  actions: {
    async getPaymentMethodService(newInstance: boolean = false): Promise<IPaymentMethodService> {
      if (this.paymentMethodService && !newInstance) {
        return this.paymentMethodService;
      } else {
        // service singleton is method-only, skip deep proxy
        const service = markRaw(await ServicesBuilder.getInstance().getService<IPaymentMethodService>(PaymentMethodService));
        this.paymentMethodService = service;
        return service;
      }
    },
    async getPaymentService(newInstance: boolean = false): Promise<IPaymentService> {
      if (this.paymentService && !newInstance) {
        return this.paymentService;
      } else {
        // service singleton is method-only, skip deep proxy
        const service = markRaw(await ServicesBuilder.getInstance().getService<IPaymentService>(PaymentService));
        this.paymentService = service;
        return service;
      }
    },
    async getTillService(): Promise<ITillService> {
      if (this.tillService) {
        return this.tillService;
      } else {
        // service singleton is method-only, skip deep proxy
        const service = markRaw(await ServicesBuilder.getInstance().getService<ITillService>(TillService));
        this.tillService = service;
        return service;
      }
    },
    getLitePosPayment(): LitePosPayment {
      // client is method-only, skip deep proxy (Raw also keeps its private members visible to tsc)
      if (!this.litePosPayment) this.litePosPayment = markRaw(new LitePosPayment());
      return this.litePosPayment;
    },
    async initialize() {
      if (this._subscription) return;
      const service = await this.getPaymentMethodService();
      const sub = service
        ?.liveQuery(() => service?.getMany({}, { pageSize: -1 }))
        .subscribe({
          next: async (result: GetManyResult<IPaymentMethod> | undefined) => {
            const selecteds: IPaymentMethod[] = [];
            const optionals: IPaymentMethod[] = [];
            result?.list.forEach((item: IPaymentMethod) => {
              if (item.is_disabled) optionals.push(item);
              else selecteds.push(item);
            });
            this.selecteds = selecteds.sort((a, b) => a.sequence - b.sequence);
            this.optionals = optionals;
          },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'payment' }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub?.unsubscribe(); this._subscription = null; } },
        });
      // service?.liveQuery may short-circuit; markRaw(undefined) throws
      if (sub) this._subscription = markRaw(sub);
    },
    stopLiveQuery() {
      this._subscription?.unsubscribe();
      this._subscription = null;
    },
    async updateStatus(paymentMethod: IPaymentMethod) {
      const service = await this.getPaymentMethodService();
      if (paymentMethod.is_disabled) {
        service.disablePaymentMethod(paymentMethod);
      } else {
        service.enablePaymentMethod(paymentMethod);
      }
    },
    async syncPaymentMethods() {
      const service = await this.getPaymentMethodService();
      service.syncStandardPaymentMethods();
    },
    async removePayment(invoice: IInvoice, payment_uid: string) {
      const service = await this.getPaymentService();
      await service.removePayment(invoice, payment_uid);
      // recompute from remaining payments so removing the last exclusive one clears the flag
      invoice.is_sales_exclusive = invoice.payments.some(p => p.payment_method.is_sales_exclusive);
    },
    async getTill(query: Partial<Till>) {
      const service = await this.getTillService();
      return await service?.getOneByParams(query);
    },

    async isHirectorReady() : Promise<boolean>{
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) return false;
      if (!currentDevice.etc.hirector_private_key || !currentDevice.etc.hirector_public_key)
        return false;
      return true;
    },

    async isPaynowReady() : Promise<boolean>{
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) return false;
      if (!currentDevice.etc.fomopay_terminal_id) return false;
      return true;
    },

    async isYeahPayReady() : Promise<boolean>{
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) return false;
      if (!currentDevice.etc.yeahpay_app_id || !currentDevice.etc.yeahpay_device_sn || !currentDevice.etc.yeahpay_server_public_key || !currentDevice.etc.yeahpay_client_private_key || !currentDevice.etc.yeahpay_signature_key) return false;
      return true;
    },
    async isLitePaymentReady(paymentMethodCode: string): Promise<boolean> {
      try {
        const config = await this.getLitePaymentConfig(paymentMethodCode);
        validateConfig(paymentMethodCode, config);
        return true;
      } catch {
        return false;
      }
    },
    // Resolve which integrated payment provider handles this method; null = not integrated
    async resolveIntegratedProvider(method?: IPaymentMethod | null): Promise<'hirector' | 'yeahpay' | 'yeahpay-scan' | 'lite' | 'paynow' | null> {
      if (!method) return null;
      const code = method.payment_method_code;
      // Refresh device config for device-keyed providers so admin key changes apply without restart; offline keeps the cached device
      if (['SPCD', 'YEAP', 'YPAO', 'YPWO', 'YPPO', 'FIUU', 'CRES'].includes(code)) await useDevice().getCurrentDevice(true).catch(() => null);
      if (code === 'SPCD' && await this.isHirectorReady()) return 'hirector';
      if (code === 'YEAP' && await this.isYeahPayReady()) return 'yeahpay';
      if ((code === 'YPAO' || code === 'YPWO' || code === 'YPPO') && await this.isYeahPayReady()) return 'yeahpay-scan';
      if (await this.isLitePaymentReady(code)) return 'lite';
      if ((code === 'PNOW' && await this.isPaynowReady()) || code === 'WPPN' || code === 'YPPN' || (code === 'ZALO' && method.is_integrated)) return 'paynow';
      return null;
    },
    async getLitePaymentConfig(paymentMethodCode: string, deviceUid?: string): Promise<Record<string, unknown>> {
      const deviceStore = useDevice();
      const device = deviceUid ? await deviceStore.getDeviceById(deviceUid) : await deviceStore.getCurrentDevice();
      if (!device) throw new SystemError('Device not found');
      const config = { ...device.etc } as Record<string, any>;
      if (paymentMethodCode === 'FIUU' && getPlatform() === 'web' && config.fiuu)
        config.fiuu.baseUrl = '/fiuu-api';
      if (paymentMethodCode === 'CRES' && getPlatform() === 'web' && config.sqb)
        config.sqb.api_url = '/sqb-api';
      return config;
    },

    /**
     * Applicable Payment Method Code: SPCD
     * @param amount the amount to be charged
     * @returns
     */
    async createHirectorPaymentCharge(amount: number): Promise<IHirectorResponse> {
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) throw new SystemError('Current device is not found');
      const config = buildHirectorConfig(currentDevice);
      const request: IHirectorCreateChargeRequest = {
        amount: amount,
        currency: 'SGD',
        callbackUrl: 'https://sysflow.wyocrm.com/webhook-test/37bab8a6-7900-4ec2-9606-d4626e3d4152',
      };
      const client = new HirectorClient(config);
      try {
        const res = await client.CreateChargeAsync(request);
        logInfo(`Create Hirector charge id ${res.order.id}, amount ${amount}`, res);
        return res;
      } catch (error) {
        logError('Hirector createCharge failed', { error, amount });
        throw error;
      }
    },

    /**
     * Applicable Payment Method Code: SPCD
     * @param chargeId the charge id to be retrieved
     * @returns
     */
    async getHirectorCharge(chargeId: number): Promise<IHirectorResponse> {
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) throw new SystemError('Current device is not found');
      const config = buildHirectorConfig(currentDevice);
      const client = new HirectorClient(config);
      const request: IHirectorGetChargeRequest = {
        id: chargeId,
        };
      const res = await client.GetChargeAsync(request);
        logInfo(`Get Hirector charge status for charge id ${chargeId}`, res);
        return res;
    },

    /**
     * Applicable Payment Method Code: YEAP
     * Create card payment charge
     * @param amount the amount to be charged
     * @param bizOrderId the business order id (invoice ref_id)
     * @returns Response with data.success: "true"/"false"/"unknown" for card payments
     *
     * Response format for card payments:
     * {
     *   success: true,
     *   data: {
     *     bizOrderId: string,
     *     orderId: string,
     *     amount: string,
     *     success: "true" | "false" | "unknown",  // Payment status
     *     cardNo: string,
     *     merchantName: string,
     *     createTime: string
     *   }
     * }
     */
    async createYeahPayCardCharge(amount: number, bizOrderId: string): Promise<IYeahPayResponse> {
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) throw new SystemError('Current device is not found');
      const config = buildYeahPayConfig(currentDevice);
      const request: IYeahPayCreateChargeRequest = {
        amount: String(preciseRound(amount, 2)),
        bizOrderId: bizOrderId,
      };
      //numberOfCopiesToPrint: Validvalues:0,1,2,3
      request.numberOfCopiesToPrint = currentDevice.etc?.yeahpay_print_copies || 0
      const client = new YeahPayClient(config);
      const res = await client.createCardCharge(request);
      return res;
    },

    /**
     * Applicable Payment Method Code: YEAP
     * Query order status
     * @param bizOrderId the business order id to be queried
     * @returns Response with data.success for card payments
     *
     * Card payment response:
     * - data.success = "true" (success)
     * - data.success = "false" (failed with errorMsg)
     * - data.success = "unknown" (processing, continue polling)
     */
    async queryYeahPayOrder(bizOrderId: string): Promise<IYeahPayResponse> {
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) throw new SystemError('Current device is not found');
      const config = buildYeahPayConfig(currentDevice);
      const request: IYeahPayQueryRequest = { bizOrderId };
      const client = new YeahPayClient(config);
      // 15s cap ensures a hung request cannot freeze the polling loop
      return withTimeout(client.queryOrder(request), YEAHPAY_QUERY_TIMEOUT_MS);
    },
    // Cancel an in-flight YeahPay order (TRADE.CANCEL.ORDER); SDK retries transient failures until maxDurationMs elapses
    async cancelYeahPayOrder(bizOrderId: string, onAttempt?: (attempt: number, res: IYeahPayResponse) => void): Promise<IYeahPayResponse> {
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) throw new SystemError('Current device is not found');
      const config = buildYeahPayConfig(currentDevice);
      const client = new YeahPayClient(config);
      return client.cancelOrder(bizOrderId, { maxDurationMs: YEAHPAY_CANCEL_MAX_DURATION_MS, timeoutMs: YEAHPAY_CANCEL_ATTEMPT_TIMEOUT_MS, onAttempt });
    },
    // Cancel YeahPay card charge for void/refund, supports cross-device refund via deviceUid
    async cancelYeahPayCardCharge(bizOrderId: string, bizRefundId: string, deviceUid?: string): Promise<IYeahPayResponse> {
      const deviceStore = useDevice();
      const device = deviceUid ? await deviceStore.getDeviceById(deviceUid) : await deviceStore.getCurrentDevice();
      if (!device) throw new SystemError('Device not found');
      const config = buildYeahPayConfig(device);
      const client = new YeahPayClient(config);
      const res = await client.cancelCardCharge({ bizOrderId, bizRefundId });
      logInfo(`Cancel YeahPay card charge for order ${bizOrderId}`, res);
      return res;
    },
    // Applicable Payment Method Code: YPAO (payWay: 'ZFBZF'), YPWO (payWay: 'WXZF'), YPPO (payWay: 'PayNowPay') — B2C terminal QR code
    async createYeahPayQRCodeCharge(amount: number, bizOrderId: string, payWay: string): Promise<IYeahPayResponse> {
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) throw new SystemError('Current device is not found');
      const config = buildYeahPayConfig(currentDevice);
      const request: IYeahPayCreateChargeRequest = {
        amount: String(preciseRound(amount, 2)),
        bizOrderId,
        payWay,
        numberOfCopiesToPrint: currentDevice.etc?.yeahpay_print_copies || 0,
      };
      const client = new YeahPayClient(config);
      const res = await client.createQRCodeCharge(request);
      if (!res.success) logError(`YeahPayQR: Create charge failed: code=${res.code}, message=${res.message}`, res);
      else logInfo(`YeahPayQR: Create charge for order ${bizOrderId}, amount ${amount}, payWay ${payWay}`, res);
      return res;
    },

    /**
     * Applicable Payment Method Code: PNOW
     * @param terminalId The FOMOPAY terminal id
     * @param invoiceRefId The invoice id (receipt number)
     * @param amount The amount to be charged
     * @returns
     */
    async createPaynowCharge(
      invoiceRefId: string,
      amount: number,
      paymentMethodCode: string
    ): Promise<IObjectResponse<ICharge>> {
      const wyoStore = useWyoSdkStore();
      const deviceStore = useDevice();
      const currentDevice = await deviceStore.getCurrentDevice();
      if (!currentDevice) throw new SystemError('Current device is not found');
      if (!currentDevice.etc.fomopay_terminal_id && paymentMethodCode === "PNOW") throw new SystemError('Fomopay terminal id is not found');
      const service = await wyoStore.getWyoService();
      const terminalId = paymentMethodCode === "ZALO" ? '' : currentDevice.etc.fomopay_terminal_id; // ZALO doesn't require terminal_id
      const description = paymentMethodCode === "ZALO" ? `DONG DAY - Payment for order ${invoiceRefId}` : 'Order payment';
      const res = await service.createCharge(terminalId, amount, invoiceRefId, description, paymentMethodCode);
      logInfo(`Create ${paymentMethodCode} charge id ${res.data?.guid}, amount ${amount}`, res);
      return res;
    },

    /**
     * Applicable Payment Method Code: PNOW
     * @param chargeId the charge id to be retrieved
     * @returns
     */
    async getPaynowCharge(chargeId: string, paymentMethodCode = 'PNOW', amount?: number): Promise<IObjectResponse<ICharge>> {
      const wyoStore = useWyoSdkStore();
      const service = await wyoStore.getWyoService();
      const res = await service.getCharge(chargeId);
      logInfo(`Get ${paymentMethodCode} charge status for charge id ${chargeId}, amount ${amount}`, res);
      return res;
    },

    /**
     * Cancel PayNow charge to invalidate QR code
     * @param chargeId the charge id to be cancelled
     * @returns
     */
    async cancelPaynowCharge(chargeId: string, paymentMethodCode = 'PNOW', amount?: number): Promise<IObjectResponse<ICharge>> {
      const wyoStore = useWyoSdkStore();
      const service = await wyoStore.getWyoService();
      const res = await service.cancelCharge(chargeId);
      logInfo(`Cancel ${paymentMethodCode} charge id ${chargeId}, amount ${amount}`, res);
      return res;
    },

    /**
     * Sync PayNow payment status to CDS using HubMessage format
     */
    async syncPaymentToCDS(
      qrCode: string,
      status: PaymentStatus,
      amount: number,
      paymentMethod: IPaymentMethod
    ) {
      try {
        const configStore = useConfigStore();
        if(!configStore?.appConfig?.config?.cds?.enabled) return;
        const deviceStore = useDevice();
        const cdsUid = await deviceStore.getCdsUid();
        const connection = DataWorker.getInstance().getConnection();
        if (connection.state === HubConnectionState.Connected && cdsUid) {
          const paymentData = {
            qr_code: qrCode,
            status: status,
            amount: amount,
            payment_method: { payment_method_code: paymentMethod.payment_method_code, name: paymentMethod.name },
            timestamp: new Date()
          };
          let messageType: HubMessageType;
          if (paymentMethod.payment_method_code === 'PNOW' || paymentMethod.payment_method_code === 'WPPN' || paymentMethod.payment_method_code === 'YPPN' || paymentMethod.payment_method_code === 'ZALO') messageType = HubMessageType.PaynowEvent;
          else if (paymentMethod.payment_method_code === 'SPCD') messageType = HubMessageType.SmoochPaymentEvent;
          else return;
          const hubMessage: HubMessage<typeof paymentData> = { type: messageType, data: paymentData };
          await connection.send('to_device', cdsUid, hubMessage);
          logInfo(`Sync ${paymentMethod.payment_method_code} payment ${status} to CDS`, hubMessage);
        }
      } catch (error) {
        captureException(error);
        console.error('Failed to sync payment to CDS:', error);
      }
    },

    /**
     * Create card payment charge using @litepos/payment library
     * Supports all payment methods integrated via @litepos/payment (FIUU, etc.)
     * @param paymentMethodCode the payment method code (e.g., 'FIUU')
     * @param amount the amount to be charged
     * @param orderId the order/invoice reference ID
     * @param currency the currency code (default: 'SGD')
     * @returns PaymentResponse with pending status (poll queryLitePayment for final result)
     */
    async createLiteCardCharge(paymentMethodCode: string, amount: number, orderId: string, currency: string = 'SGD'): Promise<PaymentResponse> {
      const config = await this.getLitePaymentConfig(paymentMethodCode);
      const payment = this.getLitePosPayment();
      const request: PaymentRequest = {
        method: paymentMethodCode,
        amount: amount,
        currency: currency,
        orderId: orderId,
        config: config,
        metadata: {}
      };
      const res = await payment.charge(request);
      logInfo(`Create ${paymentMethodCode} card charge for order ${orderId}, amount ${amount}`, res);
      return res;
    },
    /**
     * Query payment status using @litepos/payment library
     * Supports all payment methods integrated via @litepos/payment (FIUU, etc.)
     * @param paymentMethodCode the payment method code (e.g., 'FIUU')
     * @param transactionId the transaction id to query
     * @returns PaymentResponse with current status
     */
    async queryLitePayment(paymentMethodCode: string, transactionId: string): Promise<PaymentResponse> {
      const config = await this.getLitePaymentConfig(paymentMethodCode);
      const payment = this.getLitePosPayment();
      const res = await payment.queryPayment(paymentMethodCode, transactionId, config);
      logInfo(`Query ${paymentMethodCode} payment status for transaction ${transactionId}`, res);
      return res;
    },
    async cancelLitePayment(paymentMethodCode: string, transactionId: string): Promise<CancelResponse> {
      const config = await this.getLitePaymentConfig(paymentMethodCode);
      const payment = this.getLitePosPayment();
      const res = await payment.cancelPayment(paymentMethodCode, transactionId, config);
      logInfo(`Cancel ${paymentMethodCode} payment for transaction ${transactionId}`, res);
      return res;
    },
    // Refund/Void payment using @litepos/payment library (same-day void only for Fiuu)
    async refundLitePayment(paymentMethodCode: string, transactionId: string, deviceUid?: string): Promise<RefundResponse> {
      const config = await this.getLitePaymentConfig(paymentMethodCode, deviceUid);
      const payment = this.getLitePosPayment();
      const res = await payment.refund(paymentMethodCode, transactionId, config);
      logInfo(`Refund ${paymentMethodCode} payment for transaction ${transactionId}`, res);
      return res;
    },
    /**
     * Add Payment Wrapper
     * @param invoice the invoice to add the payment to
     * @param amount the amount to be charged
     * @param paymentMethod the payment method to be used
     * @param meta optional meta data to attach to the payment
     */
    async addPayment(invoice: IInvoice, amount: number, paymentMethod: IPaymentMethod, meta?: Record<string, any>) {
      const paymentService = await this.getPaymentService();
      await paymentService.addPayment(invoice, amount, paymentMethod);
      // attach meta to the last added payment
      if (meta && invoice.payments?.length > 0) {
        const lastPayment = invoice.payments[invoice.payments.length - 1] as IPayment & { meta?: Record<string, any> };
        lastPayment.meta = { ...lastPayment.meta, ...meta };
      }
      if(paymentMethod.is_sales_exclusive) invoice.is_sales_exclusive = true;
    },
  },
});
